import React, { useContext } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useBreakpoints, device } from "../../lib/breakpoints";
import { BackLocationsContext } from "../../context/BackLocationsProvider";
import mixpanel from "mixpanel-browser";
import { APP_NAME } from "../../helpers/constants";

const Logo = ({
  size = "default",
  linkTo = "/",
  showTagline = true,
  displayMode = "both",
  color = "black",
}) => {
  const { isMobile, isTabletOrAbove } = useBreakpoints();
  const { clearBackLocations } = useContext(BackLocationsContext);

  const handleNavigation = (path) => {
    clearBackLocations();
    mixpanel.track("Navigation Click", {
      destination: path,
      from_page: location.pathname,
      is_mobile: isMobile,
      is_tablet: isTabletOrAbove,
    });
  };

  const getLogoSize = () => {
    switch (size) {
      case "small":
        return { width: "32px", height: "32px" };
      case "medium":
        return { width: "48px", height: "48px" };
      case "large":
        return { width: "64px", height: "64px" };
      default:
        return { width: "40px", height: "40px" };
    }
  };

  const logoSize = getLogoSize();

  const renderLogo = () => (
    <LogoContainer>
      {(displayMode === "icon" || displayMode === "both") && (
        <LogoIcon
          src="/logo.png"
          alt={`${APP_NAME} Logo`}
          style={logoSize}
          color={color}
        />
      )}
      {(displayMode === "text" || displayMode === "both") && (
        <LogoText size={size} color={color}>
          {APP_NAME}
        </LogoText>
      )}
    </LogoContainer>
  );

  return (
    <LogoWrapper>
      {linkTo ? (
        <StyledLink to={linkTo} onClick={() => handleNavigation(linkTo)}>
          {renderLogo()}
        </StyledLink>
      ) : (
        renderLogo()
      )}
    </LogoWrapper>
  );
};

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const LogoIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: ${(props) => {
    if (props.color === "white") {
      return "brightness(0) invert(1)";
    } else if (props.color === "black") {
      return "brightness(0) invert(0)";
    }
    return "drop-shadow(0 2px 4px var(--shadow-light))";
  }};
  transition: filter 0.3s ease;
  &.logo-icon {
    color: inherit !important;
    filter: inherit !important;
  }

  @media (hover: hover) {
    &:hover {
      filter: ${(props) => {
        if (props.color === "white") {
          return "brightness(0) invert(1) drop-shadow(0 4px 8px var(--shadow-medium)) !important";
        }
        return "drop-shadow(0 4px 8px var(--shadow-light))";
      }};
    }
  }
`;

const LogoText = styled.span`
  font-family:
    "Poppins",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  font-weight: bold;
  color: ${(props) => props.color || "black"};
  font-size: ${(props) => {
    switch (props.size) {
      case "small":
        return "1.25rem";
      case "medium":
        return "1.5rem";
      case "large":
        return "2rem";
      default:
        return "1.375rem";
    }
  }};
  letter-spacing: -0.02em;
  transition: color 0.3s ease;

  @media (hover: hover) {
    &:hover {
      color: var(--color-primary);
    }
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
`;

export default Logo;
